import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ShoppingCart, 
  Music, 
  Droplets, 
  Star, 
  Check, 
  ExternalLink,
  Guitar,
  Sparkles,
  Package,
  PartyPopper,
  X,
  Percent,
  Gift
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ShareButtons from '../components/ShareButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Product data
const PRODUCTS = [
  {
    id: 'string-sauce-tin',
    name: 'String Sauce Balm Tin',
    description: 'Apply freely to hands, fingers, strings, etc., as needed for conditioning',
    price: 4.99,
    size: '15ml',
    type: 'tin',
  },
  {
    id: 'string-sauce-tube',
    name: 'String Sauce Balm Tube',
    description: 'No touch application on strings or anything else without applying to hands',
    price: 5.99,
    size: '15ml',
    type: 'tube',
  },
];

// Other apps data
const OTHER_APPS = [
  {
    name: 'TheFarmTracker',
    description: 'AI-powered farm management platform helping farmers make smarter decisions. Features include field mapping, GPS coverage tracking, weather intelligence, crop rotation planning, soil amendment tracking, and predictive analytics. Built for row crop farmers managing soybeans, corn, wheat, and cotton.',
    url: 'https://www.thefarmtracker.com',
    logo: '/images/farm-tracker-logo.jpeg',
    color: 'from-green-500/20 to-green-600/10',
    borderColor: 'border-green-500/30',
    glowColor: 'hover:shadow-green-500/20',
  },
  {
    name: 'TheForecastTracker',
    description: 'Full-featured weather app with real-time conditions, 15-day forecasts, hourly predictions, and AI-powered insights. Features accurate high/low temperatures, air quality and UV index, health recommendations, severe weather alerts, and automatic light/dark mode switching based on sunrise and sunset.',
    url: 'https://www.theforecasttracker.com',
    logo: '/images/forecast-tracker-logo.jpeg',
    color: 'from-blue-500/20 to-blue-600/10',
    borderColor: 'border-blue-500/30',
    glowColor: 'hover:shadow-blue-500/20',
  },
  {
    name: 'SportsBuzzSource',
    description: 'All-in-one sports fan platform featuring live scoreboards, news, and stats across NFL, NBA, MLB, NHL, MLS, and college sports. Engage with mini-games, collect AR trading cards through Gacha packs, and connect with fans via chat rooms and watch parties. Earn XP, unlock achievements, and climb leaderboards.',
    url: 'https://www.sportsbuzzsource.com',
    logo: '/images/sports-buzz-logo.jpg',
    color: 'from-red-500/20 to-red-600/10',
    borderColor: 'border-red-500/30',
    glowColor: 'hover:shadow-red-500/20',
  },
  {
    name: 'StringSauce',
    description: 'Professional browser-based Digital Audio Workstation (DAW) for musicians and producers. Features multi-track recording and editing, video synchronization, extensive sample library with drag-and-drop, real-time collaboration, AI-powered mixing assistance, and a marketplace for plugins and sample packs.',
    url: '/',
    logo: '/images/string-sauce-logo.png',
    color: 'from-orange-500/20 to-orange-600/10',
    borderColor: 'border-orange-500/30',
    glowColor: 'hover:shadow-orange-500/20',
    isInternal: true,
  },
];

const ProductsPage = () => {
  const [selectedProduct, setSelectedProduct] = useState(PRODUCTS[0]);
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState(null);

  // Check for success/canceled URL params (PayPal redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setShowPurchaseSuccess(true);
      setPurchaseDetails({
        productName: 'String Sauce',
        message: 'Thank you for your purchase!'
      });
      // Clean up URL
      window.history.replaceState({}, '', '/products');
    }
    if (params.get('canceled') === 'true') {
      toast.error('Purchase was canceled');
      window.history.replaceState({}, '', '/products');
    }
  }, []);

  const handlePayPalCheckout = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/products/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: selectedProduct.id,
          quantity: quantity,
          apply_bundle_discount: quantity >= 3,
        }),
      });
      
      const data = await response.json();
      
      if (data.approval_url) {
        window.location.href = data.approval_url;
      } else {
        toast.error('Failed to create order. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Checkout failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Bundle deal: 15% off when buying 3 or more
  const BUNDLE_DISCOUNT = 0.15;
  const BUNDLE_THRESHOLD = 3;
  const isBundleDeal = quantity >= BUNDLE_THRESHOLD;
  const subtotal = selectedProduct.price * quantity;
  const discount = isBundleDeal ? subtotal * BUNDLE_DISCOUNT : 0;
  const totalPrice = (subtotal - discount).toFixed(2);
  const savings = discount.toFixed(2);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Skip to main content link for keyboard users */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-orange-500 focus:text-white focus:rounded-lg"
      >
        Skip to main content
      </a>
      
      {/* Navigation */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800/50"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" aria-label="String Sauce - Home">
              <img 
                src="/images/string-sauce-logo.png" 
                alt="String Sauce logo" 
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4" role="navigation">
              <Link to="/products" className="text-orange-400 font-medium" aria-current="page">
                Products
              </Link>
              <Link to="/" className="text-zinc-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:rounded">
                DAW Studio
              </Link>
              <Link to="/pricing" className="text-zinc-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:rounded">
                Pricing
              </Link>
              <Link to="/marketplace" className="text-zinc-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:rounded">
                Marketplace
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - String Sauce Conditioner */}
      <main id="main-content">
      <section className="pt-24 pb-16 px-4" aria-labelledby="hero-heading">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full text-orange-400 text-sm mb-6">
              <Droplets className="w-4 h-4" aria-hidden="true" />
              <span>String Conditioner for Musicians</span>
            </div>
            <h1 id="hero-heading" className="text-4xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500 bg-clip-text text-transparent">
                String Sauce
              </span>
            </h1>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              Premium string conditioner that renews your strings' feel, sound, and playability. 
              Perfect for guitar, bass, and all stringed instruments.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Product Image - Enhanced */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative group"
            >
              <div className="aspect-square bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-3xl border border-zinc-700/50 flex items-center justify-center overflow-hidden relative shadow-2xl shadow-black/50 group-hover:shadow-orange-500/20 transition-all duration-500">
                {/* Glowing border effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 to-amber-500/0 group-hover:from-orange-500/10 group-hover:to-amber-500/10 transition-all duration-500 rounded-3xl" />
                
                {/* Product image */}
                <img 
                  src="/images/string-sauce-product.jpeg" 
                  alt="String Sauce - Tube and Tin" 
                  className="w-full h-full object-cover rounded-3xl transition-transform duration-700 group-hover:scale-105"
                />
                {/* String Sauce watermark overlay */}
                <img 
                  src="/images/string-sauce-watermark.png" 
                  alt="String Sauce Watermark" 
                  className="absolute inset-0 w-full h-full object-contain p-16 pointer-events-none"
                  style={{ opacity: 0.25, filter: 'drop-shadow(0 0 8px rgba(255,140,0,0.9)) drop-shadow(0 0 20px rgba(255,120,0,0.6)) drop-shadow(0 0 45px rgba(200,100,0,0.4))' }}
                />
                {/* Decorative elements */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl" />
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl" />
              </div>
            </motion.div>

            {/* Product Details & Purchase */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              {/* Benefits */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-zinc-300">Benefits</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    'Conditions strings',
                    'Renews feel & sound',
                    'Extends string life',
                    'Smooth playability',
                    'Works on all strings',
                    'Natural ingredients',
                  ].map((benefit, i) => (
                    <div key={i} className="flex items-center gap-2 text-zinc-400">
                      <Check className="w-4 h-4 text-orange-400" />
                      <span className="text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Product Selection - Enhanced */}
              <div className="space-y-3" role="radiogroup" aria-labelledby="product-style-heading">
                <h3 id="product-style-heading" className="text-lg font-semibold text-zinc-300">Choose Your Style</h3>
                <div className="grid grid-cols-2 gap-3">
                  {PRODUCTS.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => setSelectedProduct(product)}
                      role="radio"
                      aria-checked={selectedProduct.id === product.id}
                      aria-label={`${product.type === 'tube' ? 'Tube' : 'Tin'} - $${product.price}`}
                      data-testid={`product-${product.type}-btn`}
                      className={`p-3 rounded-xl border-2 transition-all duration-300 text-left group ${
                        selectedProduct.id === product.id
                          ? 'border-orange-500 bg-orange-500/10 shadow-lg shadow-orange-500/20'
                          : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-500 hover:bg-zinc-800'
                      }`}
                    >
                      <div className="w-full aspect-square rounded-lg overflow-hidden mb-2 bg-zinc-900 relative shadow-inner">
                        <img 
                          src={product.type === 'tube' ? '/images/product-tube.jpeg' : '/images/product-tin.jpeg'}
                          alt={`String Sauce ${product.type === 'tube' ? 'Tube' : 'Tin'} product image`}
                          className="w-full h-full object-cover scale-150 transition-transform duration-500 group-hover:scale-[1.6]"
                        />
                        <img 
                          src="/images/string-sauce-watermark.png" 
                          alt=""
                          aria-hidden="true"
                          className="absolute inset-0 w-full h-full object-contain p-4 pointer-events-none"
                          style={{ opacity: 0.2, filter: 'drop-shadow(0 0 6px rgba(255,140,0,0.9)) drop-shadow(0 0 15px rgba(255,120,0,0.5)) drop-shadow(0 0 30px rgba(200,100,0,0.3))' }}
                        />
                        {/* Selection indicator */}
                        {selectedProduct.id === product.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center" aria-hidden="true">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                      <div className={`font-medium transition-colors ${selectedProduct.id === product.id ? 'text-orange-400' : 'text-white'}`}>
                        {product.type === 'tube' ? 'Tube' : 'Tin'}
                      </div>
                      <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{product.description}</div>
                      <div className="text-lg font-bold text-orange-400 mt-2">${product.price}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="space-y-3" role="group" aria-labelledby="quantity-heading">
                <h3 id="quantity-heading" className="text-lg font-semibold text-zinc-300">Quantity</h3>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:bg-zinc-700 transition-colors focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    data-testid="quantity-decrease-btn"
                    aria-label="Decrease quantity"
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <span 
                    className="text-2xl font-bold w-12 text-center" 
                    aria-live="polite" 
                    aria-atomic="true"
                    data-testid="quantity-display"
                  >
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:bg-zinc-700 transition-colors focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    data-testid="quantity-increase-btn"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
                
                {/* Bundle Deal Banner */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  role="status"
                  aria-live="polite"
                  data-testid="bundle-deal-banner"
                  className={`p-3 rounded-xl border transition-all duration-300 ${
                    isBundleDeal 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-orange-500/5 border-orange-500/20'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {isBundleDeal ? (
                      <>
                        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center" aria-hidden="true">
                          <Gift className="w-4 h-4 text-green-400" />
                        </div>
                        <div>
                          <p className="text-green-400 font-semibold text-sm">Bundle Deal Applied!</p>
                          <p className="text-green-300/70 text-xs">You're saving ${savings} (15% off)</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center" aria-hidden="true">
                          <Percent className="w-4 h-4 text-orange-400" />
                        </div>
                        <div>
                          <p className="text-orange-400 font-semibold text-sm">Bundle Deal: Buy 3+, Save 15%</p>
                          <p className="text-orange-300/70 text-xs">Add {BUNDLE_THRESHOLD - quantity} more to unlock</p>
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Total & Checkout */}
              <div className="pt-4 border-t border-zinc-800" data-testid="checkout-section">
                {/* Pricing breakdown */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-500">Subtotal ({quantity} item{quantity > 1 ? 's' : ''})</span>
                    <span className="text-zinc-400">${subtotal.toFixed(2)}</span>
                  </div>
                  {isBundleDeal && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-green-400 flex items-center gap-1">
                        <Percent className="w-3 h-3" />
                        Bundle Discount (15%)
                      </span>
                      <span className="text-green-400">-${savings}</span>
                    </motion.div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                    <span className="text-zinc-300 font-medium">Total</span>
                    <div className="text-right">
                      <span className="text-3xl font-bold text-white">${totalPrice}</span>
                      {isBundleDeal && (
                        <p className="text-xs text-green-400">You save ${savings}!</p>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handlePayPalCheckout}
                  disabled={isProcessing}
                  className="w-full h-14 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold text-lg rounded-xl"
                  data-testid="buy-with-paypal-btn"
                >
                  {isProcessing ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Buy with PayPal
                    </span>
                  )}
                </Button>
                <p className="text-center text-zinc-500 text-sm mt-3">
                  Secure checkout powered by PayPal
                </p>
                
                {/* Share Product */}
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <ShareButtons 
                    variant="expanded"
                    title="String Sauce - Premium String Conditioner for Musicians"
                    description="Check out String Sauce - the premium string conditioner that renews your strings' feel, sound, and playability!"
                    url="https://stringsauce.com/products"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">
              How to Use <span className="text-orange-400">String Sauce</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Simple application for maximum results
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Step 1 - Tin */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="aspect-square rounded-2xl overflow-hidden border border-zinc-700/50 shadow-xl shadow-black/30 group-hover:shadow-orange-500/20 transition-all duration-500 relative">
                {/* Main image - full visibility */}
                <img 
                  src="/images/how-to-tin.jpeg" 
                  alt="Apply tin to fingertips" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                {/* Subtle watermark in corner only */}
                <img 
                  src="/images/string-sauce-watermark.png" 
                  alt="" 
                  aria-hidden="true"
                  className="absolute top-2 right-2 w-16 h-16 object-contain pointer-events-none"
                  style={{ opacity: 0.35, filter: 'drop-shadow(0 0 4px rgba(255,140,0,0.9)) drop-shadow(0 0 10px rgba(255,120,0,0.5))' }}
                />
                {/* Light gradient overlay - only at bottom for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                {/* Step number badge */}
                <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm shadow-lg z-10">
                  1
                </div>
                {/* Text overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-4 z-10">
                  <span className="text-orange-400 text-sm font-semibold mb-1 drop-shadow-lg">TIN</span>
                  <h3 className="text-white font-bold text-lg leading-tight drop-shadow-lg">Apply directly to fingertips</h3>
                  <p className="text-zinc-200 text-sm mt-1 drop-shadow">Wipe residue before use</p>
                </div>
              </div>
            </motion.div>

            {/* Step 2 - Tube */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="aspect-square rounded-2xl overflow-hidden border border-zinc-700/50 shadow-xl shadow-black/30 group-hover:shadow-orange-500/20 transition-all duration-500 relative">
                {/* Blurred background layer */}
                <div className="absolute inset-0">
                  <img 
                    src="/images/how-to-tube.jpeg" 
                    alt="" 
                    className="w-full h-full object-cover blur-sm scale-110"
                    aria-hidden="true"
                  />
                </div>
                {/* Focused center image */}
                <img 
                  src="/images/how-to-tube.jpeg" 
                  alt="Apply tube to strings" 
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  style={{ 
                    maskImage: 'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 80%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 80%)'
                  }}
                />
                {/* Watermark overlay */}
                <img 
                  src="/images/string-sauce-watermark.png" 
                  alt="String Sauce" 
                  className="absolute inset-0 w-full h-full object-contain p-6 pointer-events-none"
                  style={{ opacity: 0.25, filter: 'drop-shadow(0 0 6px rgba(255,140,0,0.9)) drop-shadow(0 0 15px rgba(255,120,0,0.5)) drop-shadow(0 0 30px rgba(200,100,0,0.3))' }}
                />
                {/* Gradient overlay - enhanced */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-300" />
                {/* Step number badge */}
                <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm shadow-lg z-10">
                  2
                </div>
                {/* Text overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-4 z-10">
                  <span className="text-orange-400 text-sm font-semibold mb-1 drop-shadow-lg">TUBE</span>
                  <h3 className="text-white font-bold text-lg leading-tight drop-shadow-lg">Apply directly to strings</h3>
                  <p className="text-zinc-200 text-sm mt-1 drop-shadow">No touch application</p>
                </div>
              </div>
            </motion.div>

            {/* Step 3 - Wipe */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="aspect-square rounded-2xl overflow-hidden border border-zinc-700/50 shadow-xl shadow-black/30 group-hover:shadow-orange-500/20 transition-all duration-500 relative">
                {/* Blurred background layer */}
                <div className="absolute inset-0">
                  <img 
                    src="/images/how-to-wipe.jpeg" 
                    alt="" 
                    className="w-full h-full object-cover blur-sm scale-110"
                    aria-hidden="true"
                  />
                </div>
                {/* Focused center image */}
                <img 
                  src="/images/how-to-wipe.jpeg" 
                  alt="Wipe residue" 
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  style={{ 
                    maskImage: 'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 80%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 80%)'
                  }}
                />
                {/* Watermark overlay */}
                <img 
                  src="/images/string-sauce-watermark.png" 
                  alt="String Sauce" 
                  className="absolute inset-0 w-full h-full object-contain p-6 pointer-events-none"
                  style={{ opacity: 0.25, filter: 'drop-shadow(0 0 6px rgba(255,140,0,0.9)) drop-shadow(0 0 15px rgba(255,120,0,0.5)) drop-shadow(0 0 30px rgba(200,100,0,0.3))' }}
                />
                {/* Gradient overlay - enhanced */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-300" />
                {/* Step number badge */}
                <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm shadow-lg z-10">
                  3
                </div>
                {/* Text overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-4 z-10">
                  <span className="text-orange-400 text-sm font-semibold mb-1 drop-shadow-lg">FINISH</span>
                  <h3 className="text-white font-bold text-lg leading-tight drop-shadow-lg">Wipe residue before use</h3>
                  <p className="text-zinc-200 text-sm mt-1 drop-shadow">Ready to play!</p>
                </div>
              </div>
            </motion.div>

            {/* Step 4 - Products with watermark */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="aspect-square rounded-2xl overflow-hidden border border-zinc-700/50 shadow-xl shadow-black/30 group-hover:shadow-orange-500/20 transition-all duration-500 relative">
                {/* Main product image - full visibility */}
                <img 
                  src="/images/how-to-products.jpeg" 
                  alt="String Sauce Products" 
                  className="w-full h-full object-cover object-bottom group-hover:scale-105 transition-transform duration-700"
                />
                {/* Subtle watermark in corner only */}
                <img 
                  src="/images/string-sauce-watermark.png" 
                  alt="" 
                  aria-hidden="true"
                  className="absolute top-2 right-2 w-16 h-16 object-contain pointer-events-none"
                  style={{ opacity: 0.35, filter: 'drop-shadow(0 0 4px rgba(255,140,0,0.9)) drop-shadow(0 0 10px rgba(255,120,0,0.5))' }}
                />
                {/* Light gradient overlay - only at bottom for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                {/* Step number badge */}
                <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                  <Star className="w-4 h-4" />
                </div>
                {/* Text overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-4">
                  <span className="text-orange-400 text-sm font-semibold mb-1 drop-shadow-lg">STRING SAUCE</span>
                  <h3 className="text-white font-bold text-lg leading-tight drop-shadow-lg">Premium String Care</h3>
                  <p className="text-zinc-200 text-sm mt-1 drop-shadow">Tube & Tin options</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Why Musicians Love <span className="text-orange-400">String Sauce</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Guitar,
                title: 'Works on All Strings',
                description: 'Guitar, bass, violin, ukulele - any stringed instrument benefits from String Sauce.',
              },
              {
                icon: Sparkles,
                title: 'Instant Results',
                description: 'Feel the difference immediately. Your strings will feel like new again.',
              },
              {
                icon: Droplets,
                title: 'Natural Formula',
                description: 'Made with premium natural ingredients. Safe for your hands and your instrument.',
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-zinc-800/50 rounded-2xl p-6 border border-zinc-700/50"
              >
                <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-zinc-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Apps Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">Our Other Applications</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Check out our suite of applications designed to make your life easier
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OTHER_APPS.map((app, i) => (
              <motion.a
                key={i}
                href={app.isInternal ? app.url : app.url}
                target={app.isInternal ? '_self' : '_blank'}
                rel={app.isInternal ? '' : 'noopener noreferrer'}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`group block bg-gradient-to-br ${app.color} rounded-2xl p-6 border ${app.borderColor} hover:shadow-xl ${app.glowColor} transition-all duration-300`}
              >
                <div className="aspect-square rounded-xl overflow-hidden bg-zinc-900/50 mb-4 flex items-center justify-center p-4">
                  <img 
                    src={app.logo} 
                    alt={app.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  {app.name}
                  {!app.isInternal && <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 transition-colors" />}
                </h3>
                <p className="text-sm text-zinc-400">{app.description}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-zinc-800" role="contentinfo">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-2" aria-label="String Sauce - Home">
              <img 
                src="/images/string-sauce-logo.png" 
                alt="String Sauce logo" 
                className="h-8 w-auto"
              />
            </Link>
            
            <nav aria-label="Footer navigation">
            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <Link to="/products" className="hover:text-white transition-colors">
                Products
              </Link>
              <Link to="/pricing" className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:rounded">
                Pricing
              </Link>
              <Link to="/marketplace" className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:rounded">
                Marketplace
              </Link>
              <Link to="/login" className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:rounded">
                Login
              </Link>
            </div>
            </nav>
            
            <p className="text-sm text-zinc-600">
              © {new Date().getFullYear()} String Sauce. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Purchase Success Modal */}
      <AnimatePresence>
        {showPurchaseSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowPurchaseSuccess(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-700 rounded-2xl p-8 max-w-md w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowPurchaseSuccess(false)}
                className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center">
                <PartyPopper className="w-10 h-10 text-green-400" />
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-2">
                Thank You!
              </h2>
              <p className="text-zinc-400 mb-6">
                Your order has been placed successfully. You'll receive a confirmation email shortly.
              </p>
              
              <div className="bg-zinc-800/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-zinc-500 mb-2">Share your purchase</p>
                <ShareButtons 
                  variant="expanded"
                  showLabel={false}
                  title="I just got String Sauce! Premium string conditioner for musicians."
                  description="Keep your strings feeling and sounding fresh with String Sauce!"
                  url="https://stringsauce.com/products"
                  className="justify-center"
                />
              </div>
              
              <Button
                onClick={() => setShowPurchaseSuccess(false)}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
              >
                Continue Shopping
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductsPage;
